// apps/office/src/pages/office/PlanningPricePage.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import pb from '../../lib/pocketbaseClient';
import { toast } from 'sonner';
import {
  Filter, Share2, Home, AlertCircle, LayoutGrid, List, RefreshCw, Tag,
  Ruler, Building2, Layers, Package, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(price || 0);

// Constants (fixed lists — mirrors PriceList.jsx)
const PLOT_SIZE_OPTIONS = [
  'Up to 2000 sqft',
  '2001 to 3000 sqft',
  '3001 to 4000 sqft',
  '4001 to 5000 sqft',
];

const BUILDING_TYPES = [
  'single storied',
  'Two storied (G+1)',
  'Three Storied (G+2)',
  'Four Storied (G+3)',
  'Five storied (G+4)',
];

const MAIN_CATEGORIES = ['Design Package', 'General', 'Approval Drawing & Estimate'];

const PACKAGE_OPTIONS = {
  'Design Package': ['Standard Package', 'Advance Package', 'Premium Package'],
  'General': [
    'Floor Plan (Vastu)', '3D exterior elevation', 'structure design',
    'electrical layout', 'plumbing layout', 'working cost estimate',
    '3D floor plan', 'A4 Drawing Sheet',
  ],
  'Approval Drawing & Estimate': ['panchayet approval', 'Bank loan estimate'],
};

const PlanningPricePage = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid');

  // ─── Main filters ───
  const [search, setSearch] = useState('');
  const [filterPlotSize, setFilterPlotSize] = useState('All');
  const [filterBuilding, setFilterBuilding] = useState('All');
  const [filterMainCategory, setFilterMainCategory] = useState('All');
  const [filterPackage, setFilterPackage] = useState('All');
  const [priceRange, setPriceRange] = useState([0, 100000]);

  // ─── Share modal ───
  const [isSharing, setIsSharing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sharePlotSize, setSharePlotSize] = useState('');
  const [shareBuildingType, setShareBuildingType] = useState('');
  const [shareMainCategory, setShareMainCategory] = useState('');

  // ─── Fetch ───
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setError(null);
        const records = await pb.collection('price_list_items').getFullList({
          sort: '-created',
          requestKey: null,
        });
        setProducts(records);
      } catch (err) {
        if (err.isAbort) return;
        console.error('Fetch error:', err);
        setError(err.message || 'Unable to load price list.');
        toast.error('Failed to load price list: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // ─── Max price for slider ───
  const maxPrice = useMemo(
    () => Math.max(...products.map((p) => p.sellingPrice || 0), 100000),
    [products]
  );

  // ─── Package options filtered by selected Main Category ───
  const availablePackages = useMemo(() => {
    if (filterMainCategory === 'All') {
      // Union of all packages
      const set = new Set();
      Object.values(PACKAGE_OPTIONS).forEach((arr) => arr.forEach((p) => set.add(p)));
      return ['All', ...Array.from(set)];
    }
    return ['All', ...(PACKAGE_OPTIONS[filterMainCategory] || [])];
  }, [filterMainCategory]);

  // ─── When Main Category changes, reset Package if it's not valid ───
  useEffect(() => {
    if (filterPackage !== 'All' && !availablePackages.includes(filterPackage)) {
      setFilterPackage('All');
    }
  }, [filterMainCategory, availablePackages, filterPackage]);

  // ─── Apply filters ───
  const filtered = useMemo(() => {
    let list = products;

    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(s) ||
          p.packageType?.toLowerCase().includes(s) ||
          p.plotSize?.toLowerCase().includes(s) ||
          p.buildingType?.toLowerCase().includes(s) ||
          p.mainCategory?.toLowerCase().includes(s)
      );
    }

    if (filterPlotSize !== 'All') {
      list = list.filter((p) => p.plotSize === filterPlotSize);
    }

    if (filterBuilding !== 'All') {
      list = list.filter((p) => p.buildingType === filterBuilding);
    }

    if (filterMainCategory !== 'All') {
      list = list.filter((p) => p.mainCategory === filterMainCategory);
    }

    if (filterPackage !== 'All') {
      list = list.filter((p) => p.packageType === filterPackage);
    }

    list = list.filter(
      (p) => (p.sellingPrice || 0) >= priceRange[0] && (p.sellingPrice || 0) <= priceRange[1]
    );

    return list;
  }, [products, search, filterPlotSize, filterBuilding, filterMainCategory, filterPackage, priceRange]);

  // ─── Active filter count ───
  const activeFilters = useMemo(() => {
    let n = 0;
    if (search.trim()) n++;
    if (filterPlotSize !== 'All') n++;
    if (filterBuilding !== 'All') n++;
    if (filterMainCategory !== 'All') n++;
    if (filterPackage !== 'All') n++;
    if (priceRange[1] < maxPrice) n++;
    return n;
  }, [search, filterPlotSize, filterBuilding, filterMainCategory, filterPackage, priceRange, maxPrice]);

  const clearAllFilters = () => {
    setSearch('');
    setFilterPlotSize('All');
    setFilterBuilding('All');
    setFilterMainCategory('All');
    setFilterPackage('All');
    setPriceRange([0, maxPrice]);
  };

  // ─── Share ───
  const handleShareConfirm = async () => {
    if (!sharePlotSize && !shareBuildingType && !shareMainCategory) {
      toast.error('Please select at least one filter (or leave all empty for full list)');
      return;
    }
    if (!pb.authStore.isValid || !pb.authStore.record) {
      toast.error('You must be logged in to share.');
      return;
    }
    const user = pb.authStore.record;
    setIsSharing(true);
    try {
      const token = crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).substring(2, 15);
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);

      await pb.collection('share_links').create({
        token,
        createdBy: user.id,
        expiresAt: expiresAt.toISOString(),
        isActive: true,
        plotSize: sharePlotSize || null,
        buildingType: shareBuildingType || null,
        mainCategory: shareMainCategory || null,
      });

      const shareUrl = `https://housedesigns99.com/shared-price-list/${token}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied! Valid 24h', { duration: 6000 });
      setShowShareModal(false);
      setSharePlotSize('');
      setShareBuildingType('');
      setShareMainCategory('');
    } catch (err) {
      console.error(err);
      toast.error('Failed: ' + (err?.message || 'unknown'));
    } finally {
      setIsSharing(false);
    }
  };

  // ─── Loading / Error / Empty ───
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
        <span className="ml-2 text-slate-500">Loading packages...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-red-200 p-8 max-w-2xl mx-auto mt-10">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-semibold text-lg">⚠️ Unable to load price list</p>
        <p className="text-sm text-slate-500 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Retry
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-amber-200 p-8 max-w-2xl mx-auto mt-10">
        <Home className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-amber-700 mb-2">No Packages Found</h2>
        <p className="text-slate-600">Please add items in Price List Management first.</p>
        <Button className="mt-4" onClick={() => navigate('/office/price-list')}>
          Go to Price List
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* ═══ Header ═══ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <Tag className="w-8 h-8" /> Price List &amp; Packages
            </h1>
            <p className="text-white/80">Browse and share with clients</p>
          </div>
          <Button
            onClick={() => setShowShareModal(true)}
            disabled={isSharing}
            className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30 hover:text-white gap-2"
          >
            <Share2 className="w-4 h-4" /> Share Price List (24h)
          </Button>
        </div>
      </div>

      {/* ═══ Main Content ═══ */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* ─── Sidebar Filters ─── */}
        <aside className="lg:w-80 shrink-0">
          <Card className="shadow-sm border-slate-200 lg:sticky lg:top-24">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-bold text-lg flex items-center gap-2 text-slate-700">
                  <Filter className="w-5 h-5" /> Filters
                  {activeFilters > 0 && (
                    <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                      {activeFilters}
                    </span>
                  )}
                </h2>
                {activeFilters > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1 font-medium"
                  >
                    <X className="w-3 h-3" /> Clear
                  </button>
                )}
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Search
                </label>
                <input
                  type="text"
                  placeholder="Name, package, plot size..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>

              {/* 1. Plot Size */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  <Ruler className="w-3.5 h-3.5" /> Plot Size
                </label>
                <select
                  value={filterPlotSize}
                  onChange={(e) => setFilterPlotSize(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                >
                  <option value="All">All Plot Sizes</option>
                  {PLOT_SIZE_OPTIONS.map((ps) => (
                    <option key={ps} value={ps}>{ps}</option>
                  ))}
                </select>
              </div>

              {/* 2. Building Type */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  <Building2 className="w-3.5 h-3.5" /> Building Type
                </label>
                <select
                  value={filterBuilding}
                  onChange={(e) => setFilterBuilding(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                >
                  <option value="All">All Building Types</option>
                  {BUILDING_TYPES.map((bt) => (
                    <option key={bt} value={bt}>{bt}</option>
                  ))}
                </select>
              </div>

              {/* 3. Main Category */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  <Layers className="w-3.5 h-3.5" /> Main Category
                </label>
                <select
                  value={filterMainCategory}
                  onChange={(e) => setFilterMainCategory(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                >
                  <option value="All">All Categories</option>
                  {MAIN_CATEGORIES.map((mc) => (
                    <option key={mc} value={mc}>{mc}</option>
                  ))}
                </select>
              </div>

              {/* 4. Package Type */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  <Package className="w-3.5 h-3.5" /> Package Type
                </label>
                <select
                  value={filterPackage}
                  onChange={(e) => setFilterPackage(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white"
                >
                  {availablePackages.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'All' ? 'All Packages' : opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* 5. Price Range */}
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  Max Price: <span className="text-orange-600">{formatPrice(priceRange[1])}</span>
                </label>
                <input
                  type="range"
                  min={0}
                  max={maxPrice}
                  step={500}
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([0, Number(e.target.value)])}
                  className="w-full accent-orange-500"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>₹0</span>
                  <span>{formatPrice(maxPrice)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </aside>

        {/* ─── Products ─── */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-500">
              Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of{' '}
              <span className="font-semibold text-slate-700">{products.length}</span> items
            </p>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded transition ${viewMode === 'grid' ? 'bg-white shadow' : 'hover:bg-slate-200'}`}
                aria-label="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded transition ${viewMode === 'list' ? 'bg-white shadow' : 'hover:bg-slate-200'}`}
                aria-label="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No packages match your filters.</p>
              <button
                onClick={clearAllFilters}
                className="mt-3 text-sm text-orange-600 hover:text-orange-800 underline"
              >
                Clear all filters
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((p) => {
                const discountPercent = p.discountPercent || 0;
                return (
                  <motion.div
                    key={p.id}
                    whileHover={{ y: -4 }}
                    className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="h-48 bg-slate-100 relative">
                      {p.image ? (
                        <img
                          src={pb.files.getUrl(p, p.image)}
                          alt={p.name || p.packageType}
                          className="w-full h-full object-cover"
                          onError={(e) => (e.target.src = 'https://via.placeholder.com/400x300?text=No+Image')}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-slate-200">
                          <Home className="w-12 h-12 text-slate-400" />
                        </div>
                      )}
                      {discountPercent > 0 && (
                        <Badge className="absolute top-3 right-3 bg-amber-500 text-white border-0">
                          {discountPercent}% OFF
                        </Badge>
                      )}
                      {p.plotSize && (
                        <Badge className="absolute top-3 left-3 bg-sky-600 text-white border-0 text-[10px]">
                          {p.plotSize}
                        </Badge>
                      )}
                      {p.buildingType && (
                        <Badge className="absolute bottom-3 left-3 bg-black/60 text-white border-0 capitalize">
                          {p.buildingType}
                        </Badge>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <h3 className="font-semibold text-slate-800 line-clamp-2 flex-1">
                          {p.name || p.packageType}
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                          p.mainCategory === 'Design Package' ? 'bg-violet-100 text-violet-700' :
                          p.mainCategory === 'Approval Drawing & Estimate' ? 'bg-fuchsia-100 text-fuchsia-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {p.mainCategory}
                        </span>
                        {p.packageType && (
                          <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            {p.packageType}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-2xl font-bold text-amber-600">
                          {formatPrice(p.sellingPrice)}
                        </span>
                        {p.mrp > p.sellingPrice && (
                          <span className="text-sm line-through text-slate-400 ml-2">
                            {formatPrice(p.mrp)}
                          </span>
                        )}
                      </div>
                      {p.unit && <p className="text-xs text-slate-500 mt-1">Unit: {p.unit}</p>}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((p) => (
                <div
                  key={p.id}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition flex flex-wrap md:flex-nowrap gap-4 items-center"
                >
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 shrink-0">
                    {p.image ? (
                      <img
                        src={pb.files.getUrl(p, p.image)}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800">{p.name || p.packageType}</h3>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {p.plotSize && (
                        <span className="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full font-medium">
                          {p.plotSize}
                        </span>
                      )}
                      <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full capitalize">
                        {p.buildingType}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        p.mainCategory === 'Design Package' ? 'bg-violet-100 text-violet-700' :
                        p.mainCategory === 'Approval Drawing & Estimate' ? 'bg-fuchsia-100 text-fuchsia-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {p.mainCategory}
                      </span>
                      {p.packageType && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                          {p.packageType}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-xl font-bold text-amber-600">
                      {formatPrice(p.sellingPrice)}
                    </span>
                    {p.mrp > p.sellingPrice && (
                      <span className="text-xs line-through text-slate-400 ml-1">
                        {formatPrice(p.mrp)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ═══ Share Modal ═══ */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-orange-600">
              📤 Share Price List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium text-slate-700">1. Plot Size</Label>
              <select
                value={sharePlotSize}
                onChange={(e) => setSharePlotSize(e.target.value)}
                className="w-full mt-1.5 px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white"
              >
                <option value="">— Any plot size —</option>
                {PLOT_SIZE_OPTIONS.map((ps) => (
                  <option key={ps} value={ps}>{ps}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">2. Building Type</Label>
              <select
                value={shareBuildingType}
                onChange={(e) => setShareBuildingType(e.target.value)}
                className="w-full mt-1.5 px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white"
              >
                <option value="">— Any building type —</option>
                {BUILDING_TYPES.map((bt) => (
                  <option key={bt} value={bt}>{bt}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">3. Main Category</Label>
              <select
                value={shareMainCategory}
                onChange={(e) => setShareMainCategory(e.target.value)}
                className="w-full mt-1.5 px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white"
              >
                <option value="">— Any category —</option>
                {MAIN_CATEGORIES.map((mc) => (
                  <option key={mc} value={mc}>{mc}</option>
                ))}
              </select>
            </div>

            <p className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              💡 <strong>Optional:</strong> Leave blank to include everything. Client only sees what's filtered here.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleShareConfirm}
              disabled={(!sharePlotSize && !shareBuildingType && !shareMainCategory) || isSharing}
              className="bg-gradient-to-r from-orange-500 to-amber-500 text-white"
            >
              {isSharing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Share2 className="w-4 h-4 mr-2" /> Generate &amp; Copy Link</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlanningPricePage;
